<script setup lang="ts">
import { useAppToast } from '@/composables/useAppToast'
definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const { setPageTitle } = useBreadcrumbs()
const toast = useAppToast()

const introKey = ref(0)

// --- Backfill student products ---
interface BackfillResult {
  total: number
  synced?: number
  updated?: number
  skipped: number
  failed: number
}

const isBackfillingProducts = ref(false)
const productsResult = ref<BackfillResult | null>(null)

async function backfillStudentProducts() {
  isBackfillingProducts.value = true
  productsResult.value = null
  try {
    const { data } = await axios.post<BackfillResult>(
      '/api/admin/toolbox/backfill-student-products',
    )
    productsResult.value = data
    toast.add({
      color: 'success',
      title: 'Done',
      description: `Synced products for ${data.synced} of ${data.total} students.`,
      duration: 5000,
    })
  } catch {
    toast.add({
      color: 'error',
      title: 'Error',
      description: 'Product backfill failed.',
      duration: 5000,
    })
  } finally {
    isBackfillingProducts.value = false
  }
}

// --- Backfill student profiles ---
const isBackfillingProfiles = ref(false)
const profilesResult = ref<BackfillResult | null>(null)

async function backfillStudentProfiles() {
  isBackfillingProfiles.value = true
  profilesResult.value = null
  try {
    const { data } = await axios.post<BackfillResult>(
      '/api/admin/toolbox/backfill-student-profiles',
    )
    profilesResult.value = data
    toast.add({
      color: 'success',
      title: 'Done',
      description: `Updated profiles for ${data.updated} of ${data.total} students.`,
      duration: 5000,
    })
  } catch {
    toast.add({
      color: 'error',
      title: 'Error',
      description: 'Profile backfill failed.',
      duration: 5000,
    })
  } finally {
    isBackfillingProfiles.value = false
  }
}

onMounted(() => {
  setPageTitle('Toolbox')
})
</script>

<template>
  <div class="space-y-6">
    <div>
      <h1 class="text-2xl font-bold">Toolbox</h1>
      <p class="text-muted">One-off maintenance actions and data backfills</p>
    </div>

    <!-- Student Data Backfills -->
    <AppPanel header="Student Data Backfills">
      <div class="space-y-6">
        <!-- Backfill Products -->
        <div
          class="rounded-border flex flex-col gap-3 border border-neutral-200 p-4 dark:border-neutral-700"
        >
          <div>
            <p class="font-semibold">Backfill Campus Products</p>
            <p class="text-sm text-muted">
              Syncs Campus degree/product data for all KIT students who have never logged in or
              whose product data is missing. Uses the current active semester.
            </p>
          </div>
          <div class="flex items-center gap-3">
            <UButton
              label="Run"
              icon="i-lucide-refresh-cw"
              :loading="isBackfillingProducts"
              :disabled="isBackfillingProducts"
              @click="backfillStudentProducts"
            />
            <div v-if="productsResult" class="flex gap-4 text-sm">
              <span class="text-neutral-600 dark:text-neutral-300">
                Total: <strong>{{ productsResult.total }}</strong>
              </span>
              <span class="text-green-700 dark:text-green-400">
                Synced: <strong>{{ productsResult.synced }}</strong>
              </span>
              <span class="text-muted">
                Skipped: <strong>{{ productsResult.skipped }}</strong>
              </span>
              <span v-if="productsResult.failed > 0" class="text-red-700 dark:text-red-400">
                Failed: <strong>{{ productsResult.failed }}</strong>
              </span>
            </div>
          </div>
        </div>

        <!-- Backfill Profiles -->
        <div
          class="rounded-border flex flex-col gap-3 border border-neutral-200 p-4 dark:border-neutral-700"
        >
          <div>
            <p class="font-semibold">Backfill Student Profiles</p>
            <p class="text-sm text-muted">
              Fetches matriculation ID and salutation from Campus for KIT students whose profile is
              incomplete. Only updates fields that are currently empty.
            </p>
          </div>
          <div class="flex items-center gap-3">
            <UButton
              label="Run"
              icon="i-lucide-refresh-cw"
              :loading="isBackfillingProfiles"
              :disabled="isBackfillingProfiles"
              @click="backfillStudentProfiles"
            />
            <div v-if="profilesResult" class="flex gap-4 text-sm">
              <span class="text-neutral-600 dark:text-neutral-300">
                Total: <strong>{{ profilesResult.total }}</strong>
              </span>
              <span class="text-green-700 dark:text-green-400">
                Updated: <strong>{{ profilesResult.updated }}</strong>
              </span>
              <span class="text-muted">
                Skipped: <strong>{{ profilesResult.skipped }}</strong>
              </span>
              <span v-if="profilesResult.failed > 0" class="text-red-700 dark:text-red-400">
                Failed: <strong>{{ profilesResult.failed }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>
    </AppPanel>

    <!-- Loader preview -->
    <AppPanel header="Loader Preview">
      <div class="space-y-6">
        <div v-for="variant in ['flat', '3d'] as const" :key="variant" class="space-y-2">
          <p class="font-semibold">{{ variant }}</p>
          <div class="flex flex-wrap items-end gap-8">
            <div
              v-for="size in ['xs', 'sm', 'md', 'lg', 'xl', '2xl'] as const"
              :key="size"
              class="flex flex-col items-center gap-2"
            >
              <AppLoader :size="size" :variant="variant" />
              <span class="text-xs text-muted">{{ size }}</span>
            </div>
          </div>
        </div>
        <div class="space-y-2">
          <div class="flex items-center gap-3">
            <p class="font-semibold">intro</p>
            <UButton
              label="Replay"
              icon="i-lucide-rotate-ccw"
              size="xs"
              variant="soft"
              @click="introKey++"
            />
          </div>
          <div :key="introKey" class="flex flex-wrap items-end gap-8">
            <AppLoader
              v-for="variant in ['flat', '3d'] as const"
              :key="variant"
              size="2xl"
              :variant="variant"
              intro
            />
          </div>
        </div>
      </div>
    </AppPanel>

    <!-- Spinner preview -->
    <AppPanel header="Spinner Preview">
      <div class="space-y-6">
        <div
          v-for="variant in ['flat', '3d', 'orbit', 'dots', 'cube', 'helix'] as const"
          :key="variant"
          class="space-y-2"
        >
          <p class="font-semibold">{{ variant }}</p>
          <div class="flex flex-wrap items-end gap-8">
            <div
              v-for="size in ['xs', 'sm', 'md', 'lg', 'xl'] as const"
              :key="size"
              class="flex flex-col items-center gap-2"
            >
              <AppSpinner :size="size" :variant="variant" />
              <span class="text-xs text-muted">{{ size }}</span>
            </div>
          </div>
        </div>
      </div>
    </AppPanel>
  </div>
</template>
